import * as React from 'react'
import { I18n } from "react-redux-i18n";
import { Modal, ModalFooter, ModalTitle, ModalBody, ModalHeader } from '@common/components/modal';;

import { Button } from '@common/components/Button';
import { FormControl } from '@common/components/form-control';
import { Radio } from '@common/components/radio';
import { getTestAttributes } from "@common/utils/tests";
import { Flex } from "@common/components/Flex";
import TreeDropdown from "@common/components/TreeDropdown";
import { FolderListDto } from "@common/dto/list-folder";
import { HttpErrorDto } from "@common/dto/http";
import { MODULE_NAME } from "@modules/individuals";
import { checkIndividualNameExists, createIndividual } from "@modules/individuals/actions/individuals";

import { IndividualCreateDto, IndividualGender } from "../../dto/create-individual";

import './new-individual.styles.scss';

type NewIndividualProps = {
  onClose: () => void;
  onSubmit: (globalId: number) => void;
  currentFolderId: FolderListDto['id'];
  errors?: string[];
  folders: FolderListDto[];
  showErrorMessage: (message?: string) => void;
};

export const NewIndividual = ({
  folders,
  currentFolderId,
  onClose,
  onSubmit,
  showErrorMessage
}: NewIndividualProps) => {


  const [errors, setErros] = React.useState<{individualName: string[]}>({ individualName: [] });
  const [submitting, setSubmitting] = React.useState(false);

  const [state, setState] = React.useState<IndividualCreateDto>({
    name: '',
    gender: IndividualGender.male,
    homeFolderId: currentFolderId
  });

  const handleFormFields = React.useCallback((newFieldValue: Partial<IndividualCreateDto>) => {
    setState( (prevState) => ({
      ...prevState,
      ...newFieldValue
    }));
  }, [setState]);

  const handleGenderField = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    handleFormFields({
      gender: parseInt(e.target.value),
    });
  }, [handleFormFields]);

  const handleNewIndividualName = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    handleFormFields({
      name: e.target.value,
    });
    setErros((prev) => ({
      ...prev,
      individualName: []
    }));
  }, [handleFormFields]);

  const handleFolderId = React.useCallback((homeFolderId: number) => {
    handleFormFields({
      homeFolderId
    });
  }, [handleFormFields]);

  const createNewIndividual = React.useCallback(async (newIndividualForm: IndividualCreateDto) => {
    try {
      setSubmitting(true);

      if(await checkIndividualNameExists(newIndividualForm)) {
        showErrorMessage(I18n.t("Individual name already exists in this folder"));
        return;
      }

      const createdIndividual = await createIndividual(newIndividualForm);
      if(createdIndividual.errors?.length) {
        showErrorMessage(createdIndividual.errors[0]);
        return;
      }

      onSubmit(createdIndividual.data);
    } catch (err) {
      showErrorMessage((err as HttpErrorDto)?.entity);
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  }, [onSubmit, showErrorMessage, onClose]);

  const handleSubmit = React.useCallback( () => {
    if (!state.name) {
      setErros((prev) => ({
        ...prev,
        individualName: [
          'Specify name of new individual'
        ]
      }));
    } else {
      createNewIndividual(state);
    }


  }, [state, setState, createNewIndividual]);

    return (
        <Modal
          show
          bsSize="lg"
          bsClass="NewIndividual bootstrap modal"
          onHide={onClose}
          onEscapeKeyDown={onClose}
          animation={false}
        >
          <ModalHeader closeButton onHide={onClose}>
            <ModalTitle
              {...getTestAttributes(MODULE_NAME, 'new-individual-modal-title', 'header')}
            >
              {I18n.t("Add New Individual")}
            </ModalTitle>
          </ModalHeader>
          <ModalBody>
            <Flex justify="center" alignItems="center" className="NewIndividual__content">
              <div className="NewIndividual__content__margined-section">
                <Flex>
                  <div className="NewIndividual__content__form-label">
                    {I18n.t('Folder Name')}:
                  </div>
                  <div className="NewIndividual__content__form-field">
                    <TreeDropdown
                      className="NewIndividual__content__folders"
                      items={folders}
                      value={state.homeFolderId}
                      onChange={handleFolderId}
                      {...getTestAttributes(MODULE_NAME, 'home-folder-id', 'select')}
                    />
                  </div>
                </Flex>
                <Flex>
                  <div className="NewIndividual__content__form-label">
                    {I18n.t('Individual Name')}:
                  </div>
                  <div className="NewIndividual__content__form-field">
                    <FormControl
                      type="text"
                      defaultValue={state.name}
                      onChange={handleNewIndividualName}
                      className={errors.individualName?.length > 0 ? 'NewIndividual__content__input-error' : ''}
                      {...getTestAttributes({ id: "tx-ind-name" })}
                    />
                    {errors.individualName?.map((error) => (
                      <div className="NewIndividual__content__errors" key={error}>
                        {error}
                      </div>
                    ))}
                  </div>
                </Flex>
                <Flex>
                  <div className="NewIndividual__content__form-label">
                    {I18n.t('Sex')}:
                  </div>
                  <div className="NewIndividual__content__form-field">
                    <div>
                      <Radio
                        className="NewIndividual__content__gender-field"
                        name="gender"
                        value={IndividualGender.male}
                        defaultChecked
                        onChange={handleGenderField}
                        {...getTestAttributes({ id: "radio-ind-male-input" })}
                      >
                        {I18n.t('Male')}
                      </Radio>
                      <Radio
                        className="NewIndividual__content__gender-field"
                        name="gender"
                        value={IndividualGender.female}
                        onChange={handleGenderField}
                        {...getTestAttributes({ id: "radio-ind-female-input" })}
                      >
                        {I18n.t('Female')}
                      </Radio>
                      <Radio
                          className="NewIndividual__content__gender-field"
                          name="gender"
                          value={IndividualGender.unknown}
                          onChange={handleGenderField}
                          {...getTestAttributes({ id: "radio-ind-unknown-input" })}
                      >
                        {I18n.t('Unknown')}
                      </Radio>
                    </div>
                  </div>
                </Flex>
              </div>
            </Flex>
          </ModalBody>
          <ModalFooter>
            <Flex justify="space-between">
              <Button
                onClick={onClose}
                {...getTestAttributes(MODULE_NAME, 'new-individual-modal-close', 'button')}
              >
                {I18n.t('Cancel')}
              </Button>
              <Flex justify="space-between">
                <Button
                  {...getTestAttributes(MODULE_NAME, 'new-individual-launch-new-questionnaire', 'button')}
                >
                  {I18n.t('Launch New Questionnaire')}
                </Button>
                <Button
                  bsStyle="primary"
                  onClick={handleSubmit}
                  disabled={submitting || Boolean(errors.individualName?.length)}
                  {...getTestAttributes({ id: "btn-ind-save" })}
                >
                  {I18n.t('Create')}
                </Button>
              </Flex>
            </Flex>
          </ModalFooter>
        </Modal>
    )
};